import React from "react";
import { Chip } from "./frames";
import { Database, Shield, ListChecks } from "lucide-react";

// ---------- SCHEMA ----------
const COLLECTIONS = [
  { name: "users", desc: "Auth accounts (all roles)", fields: ["_id", "email (unique)", "password_hash", "role: candidate|employer|admin", "email_verified: bool", "created_at", "last_login"] },
  { name: "email_otps", desc: "OTP / verification links", fields: ["_id", "user_id", "code_hash", "purpose: verify|reset", "expires_at", "consumed_at", "attempts"] },
  { name: "candidate_profiles", desc: "One per candidate", fields: ["_id", "user_id", "alias (HG-###)", "status: draft|submitted|under_review|changes_requested|approved|withdrawn", "private: {legal_name, phone, exact_address, socials}", "professions[]: groomer|rider|jockey|farrier|trainer|…", "role_skills: { profession -> skills[] } (role-specific)", "public: {broad_location, experience, languages[], availability, training, preferred_countries[]}", "completeness: int", "approved_snapshot", "review_reason", "approved_at", "updated_at"] },
  { name: "profile_revisions", desc: "Pending edits to approved profiles", fields: ["_id", "profile_id", "diff / full_pending_public", "status: pending|approved|rejected", "submitted_at", "reviewed_by", "review_reason"] },
  { name: "media_assets", desc: "Private files (object storage)", fields: ["_id", "owner_user_id", "profile_id", "kind: cv|certificate|intro_video|work_video", "storage_key (private)", "mime", "size_bytes", "duration_s", "thumbnail_key", "scan_status", "active: bool", "created_at"] },
  { name: "employers", desc: "Organization + authorized contact", fields: ["_id", "user_id", "org_name", "reg_number", "country", "contact_name", "contact_role", "status: pending|verified|revoked", "review_reason", "verified_at"] },
  { name: "jobs", desc: "Postings", fields: ["_id", "employer_id", "title", "profession: target role", "location", "type", "description", "criteria_mandatory[] (per-job)", "criteria_preferred[]", "status: draft|submitted|approved|rejected|closed", "reviewed_by", "created_at"] },
  { name: "applications", desc: "Candidate ↔ job", fields: ["_id", "job_id", "candidate_id", "profile_snapshot (approved)", "current_stage", "status: active|declined|withdrawn", "eligibility_checked", "created_at", "UNIQUE(job_id, candidate_id) while active"] },
  { name: "application_stages", desc: "Stage history / audit", fields: ["_id", "application_id", "stage", "responsible: candidate|employer|admin|authority", "note", "due_date?", "required_docs[]", "decision (for visa/ZAV/recognition)", "actor_user_id", "created_at"] },
  { name: "posts", desc: "Community feed", fields: ["_id", "author_user_id", "category", "body", "media[] (public-safe)", "status: pending|approved|removed", "pinned: bool", "created_at"] },
  { name: "comments", desc: "On posts", fields: ["_id", "post_id", "author_user_id", "body", "created_at"] },
  { name: "reports", desc: "Moderation reports", fields: ["_id", "target_type: post|comment", "target_id", "reporter_user_id", "reason", "status: open|actioned|dismissed"] },
  { name: "media_access_logs", desc: "Employer views (rate-limit + audit)", fields: ["_id", "employer_id", "candidate_id", "asset_id?", "action: search_view|profile_view|cert_preview", "ip", "created_at"] },
  { name: "audit_logs", desc: "All privileged actions", fields: ["_id", "actor_user_id", "actor_role", "action", "entity_type", "entity_id", "meta", "created_at"] },
  { name: "notifications", desc: "Action-needed / status change", fields: ["_id", "user_id", "type", "title", "body", "link", "read: bool", "created_at"] },
];

export const SchemaSection = () => (
  <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
    {COLLECTIONS.map((c) => (
      <div key={c.name} className="rounded-md border border-[#E8E4DB] bg-white p-4">
        <div className="flex items-center gap-2">
          <Database className="h-4 w-4 text-[#C4A46B]" />
          <span className="font-mono text-[13px] font-semibold text-[#173A2C]">{c.name}</span>
        </div>
        <div className="mt-0.5 text-[11px] text-[#5C5855]">{c.desc}</div>
        <ul className="mt-2 space-y-1">
          {c.fields.map((f) => (
            <li key={f} className="font-mono text-[10px] leading-snug text-[#2C2A29]"><span className="text-[#C4A46B]">·</span> {f}</li>
          ))}
        </ul>
      </div>
    ))}
  </div>
);

// ---------- PERMISSIONS ----------
const CAPS = [
  ["View public feed & approved jobs", "✔ (public)", "✔", "✔", "✔"],
  ["Sign up + verify email (OTP/link)", "✔", "✔", "—", "—"],
  ["Create / edit candidate profile", "✖", "✔ own", "✖", "✔ any"],
  ["Submit profile for review", "✖", "✔", "✖", "—"],
  ["Approve / request changes on profile", "✖", "✖", "✖", "✔"],
  ["Appear in employer search", "✖", "only if approved", "✖", "n/a"],
  ["Upload private media (CV/certs/video)", "✖", "✔ own", "✖", "view for review"],
  ["Register organization", "✖", "✖", "✔", "—"],
  ["Search / view candidates (masked)", "✖", "✖", "only if verified", "✔"],
  ["Download original CV", "✖", "✖", "✖ (no endpoint)", "✖ (no endpoint)"],
  ["Preview certificate (masked, logged)", "✖", "✖", "if permitted", "✔"],
  ["Post a job (needs approval)", "✖", "✖", "✔ if verified", "✔"],
  ["Apply to a job", "✖", "if eligible", "✖", "—"],
  ["Update hiring stages", "✖", "✖", "✔ assigned", "✔ all"],
  ["Control visa / ZAV / recognition stages", "✖", "✖", "✖", "✔"],
  ["Moderate feed / reports / pin resources", "✖", "✖", "✖", "✔"],
  ["Read audit logs", "✖", "✖", "✖", "✔"],
];

export const PermissionsSection = () => (
  <div className="overflow-x-auto rounded-md border border-[#E8E4DB] bg-white thin-scroll">
    <table className="w-full min-w-[720px] text-left text-[12px]">
      <thead>
        <tr className="border-b border-[#E8E4DB] bg-[#F4F1EA] text-[#173A2C]">
          <th className="px-4 py-3 font-semibold">Capability</th>
          <th className="px-3 py-3 font-semibold">Public</th>
          <th className="px-3 py-3 font-semibold">Candidate</th>
          <th className="px-3 py-3 font-semibold">Employer</th>
          <th className="px-3 py-3 font-semibold">Admin</th>
        </tr>
      </thead>
      <tbody>
        {CAPS.map((row, i) => (
          <tr key={i} className="border-b border-[#E8E4DB] last:border-0">
            <td className="px-4 py-2.5 text-[#2C2A29]">{row[0]}</td>
            {row.slice(1).map((cell, j) => (
              <td key={j} className={`px-3 py-2.5 ${cell.startsWith("✔") ? "text-[#2E6B4B]" : cell.startsWith("✖") ? "text-[#8B3A3A]" : "text-[#5C5855]"}`}>{cell}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
    <div className="border-t border-[#E8E4DB] px-4 py-2 text-[11px] text-[#5C5855]">
      <Shield className="mr-1 inline h-3.5 w-3.5 text-[#1F4A38]" /> Every rule above is enforced by backend APIs — not just hidden buttons.
    </div>
  </div>
);

// ---------- PHASES ----------
const PHASES = [
  { n: 1, title: "Foundation & Auth", tone: "green", items: ["JWT auth (email + password)", "Email OTP / verification link (Resend)", "Role model, admin seed, sessions", "Staging vs production settings, PWA shell"], tests: ["Sign up → receive OTP → verify → login persists", "Protected routes reject missing/invalid token"] },
  { n: 2, title: "Candidate Product", tone: "green", items: ["Profile editor + completeness checklist", "Private media uploads (object storage) with client+server validation", "Submit → review workflow, status states", "Pending revisions on approved profiles"], tests: ["AT1: profile & files persist across logout/login", "AT2: oversized/unsupported upload rejected by server", "AT4: pending edits never visible pre-approval"] },
  { n: 3, title: "Employer & Talent Access", tone: "gold", items: ["Org registration + admin verification", "Backend-gated talent search (verified only)", "Masked profiles (HG-###), rate-limit + access logs", "No CV endpoint; masked, logged cert previews"], tests: ["AT3: unapproved candidate absent from API results", "AT5: unverified employer blocked at API", "AT6: only masked profiles, no original CV retrievable"] },
  { n: 4, title: "Jobs, Applications & Journey", tone: "gold", items: ["Job posting + admin review, per-job criteria", "Server-enforced eligibility with exact reasons", "Duplicate-application prevention + approved snapshot", "Configurable stage engine + audit trail + notifications"], tests: ["AT7: ineligible applicant gets exact reason & blocked via API", "AT8: real persistent stages across multiple applications"] },
  { n: 5, title: "Community & Operations", tone: "amber", items: ["Moderated feed (7 categories), comments, reports", "Admin moderation, pin resources, remove posts", "Audit log surface + notifications center", "Backup/export & recovery procedures"], tests: ["AT9: approvals, doc views & stage changes create audit records", "AT10: feed + jobs on desktop/phone; PWA installs"] },
];

export const PhasesSection = () => (
  <div className="space-y-4">
    <div className="rounded-md border border-[#1F4A38]/25 bg-[#1F4A38]/6 p-3 text-[12px] text-[#2C2A29]">
      <b className="text-[#1F4A38]">Phase 0 — Product lock (this page).</b> Approve designs, schema, permissions and sequence before build. Each later phase preserves prior functionality and is verified against its acceptance tests before moving on.
    </div>
    {PHASES.map((p) => (
      <div key={p.n} className="rounded-md border border-[#E8E4DB] bg-white p-4">
        <div className="flex items-center gap-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#1F4A38] text-[13px] font-bold text-white">{p.n}</div>
          <div className="font-serif text-lg text-[#173A2C]">{p.title}</div>
          <Chip tone={p.tone}>Phase {p.n}</Chip>
        </div>
        <div className="mt-3 grid gap-4 md:grid-cols-2">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#C4A46B]">Build</div>
            <ul className="mt-1 space-y-1">
              {p.items.map((it) => <li key={it} className="text-[12px] text-[#2C2A29]">• {it}</li>)}
            </ul>
          </div>
          <div>
            <div className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#C4A46B]"><ListChecks className="h-3.5 w-3.5" /> Acceptance tests</div>
            <ul className="mt-1 space-y-1">
              {p.tests.map((t) => <li key={t} className="text-[12px] text-[#2E6B4B]">✓ {t}</li>)}
            </ul>
          </div>
        </div>
      </div>
    ))}
  </div>
);
